import type {
  AiRetrievalBundle,
  AiRetrievalCacheAdapter,
  AiRetrievalCacheDocument,
  AiRetrievalRequest,
  AiRetrievalScope,
} from './retrievalTypes'

const CACHE_TTL_MS = 5 * 60 * 1000

export const hashRetrievalText = (value: string) => {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(36)
}

export const retrievalCacheKey = (request: AiRetrievalRequest, scopes: AiRetrievalScope[]) => {
  const targetKey = (request.selectedTargets ?? [])
    .map((target) => `${target.type}:${target.id}`)
    .sort()
    .join('|')
  return [
    'ai_context_cache',
    request.workspaceId,
    hashRetrievalText(request.query.trim().toLowerCase()),
    request.activeBoard ?? 'ANY_BOARD',
    hashRetrievalText(targetKey || 'NO_TARGETS'),
    scopes.slice().sort().join(','),
  ].join('::')
}

export const aiContextCacheDocPath = (cacheKey: string) =>
  `ai_context_cache/${hashRetrievalText(cacheKey)}`

export const createRetrievalCacheDocument = (
  bundle: AiRetrievalBundle,
  cacheKey: string,
  scopes: AiRetrievalScope[],
  nowIso: string,
): AiRetrievalCacheDocument => {
  const expiresAtIso = new Date(new Date(nowIso).getTime() + CACHE_TTL_MS).toISOString()
  const { cache: _cache, ...diagnostics } = bundle.diagnostics
  void _cache
  return {
    version: 1,
    workspaceId: bundle.workspaceId,
    cacheKey,
    scopeKeys: scopes,
    queryHash: bundle.queryHash,
    visibility: 'PRIVATE',
    digest: {
      summary: bundle.digest.summary,
      includedRecordIds: bundle.digest.includedRecordIds,
      omittedReason: bundle.digest.omittedReason,
      generatedAtIso: bundle.digest.generatedAtIso,
    },
    slices: bundle.slices,
    diagnostics,
    expiresAtIso,
    createdAtIso: nowIso,
    updatedAtIso: nowIso,
  }
}

export const isRetrievalCacheFresh = (document: AiRetrievalCacheDocument, nowIso: string) =>
  new Date(document.expiresAtIso).getTime() > new Date(nowIso).getTime()

export const createInMemoryRetrievalCache = (): AiRetrievalCacheAdapter => {
  const entries = new Map<string, AiRetrievalCacheDocument>()
  return {
    get: (cacheKey, nowIso) => {
      const entry = entries.get(cacheKey)
      if (!entry) return null
      if (!isRetrievalCacheFresh(entry, nowIso)) {
        entries.delete(cacheKey)
        return null
      }
      return entry
    },
    set: (document) => {
      entries.set(document.cacheKey, document)
    },
  }
}

export const memoryRetrievalCache = createInMemoryRetrievalCache()
